// src/services/proveedoresService.js
import {
  collection, addDoc, getDoc, getDocs, doc, updateDoc, onSnapshot,
  query, where, orderBy, limit as qlimit, startAt, endAt, serverTimestamp
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";

const COLL = "proveedores";

// === Realtime list ===
export function listenProveedores({ soloActivos = true } = {}, cb) {
  const base = collection(db, COLL);
  const qy = soloActivos ? query(base, where("activo", "==", true)) : base;
  return onSnapshot(qy, (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

// === Crear / Alta rápida ===
export async function createProveedor(data) {
  const nombre = (data?.nombre || "").trim();
  // Defensa en profundidad: no permitir proveedores sin nombre (igual que
  // createProveedorRapido). Evita basura en el catálogo si el form falla.
  if (!nombre) throw new Error("El nombre del proveedor es obligatorio");
  const payload = {
    nombre,
    nombreLC: nombre.toLowerCase(),        // clave para búsqueda
    contacto: data?.contacto || "",
    telefono: data?.telefono || "",
    email: data?.email || "",
    rfc: data?.rfc || "",
    direccion: data?.direccion || "",
    notas: data?.notas || "",
    activo: data?.activo ?? true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COLL), payload);
  return ref;
}

// Alta súper-rápida solo con nombre (para el autosuggest de CxP)
export async function createProveedorRapido(nombre) {
  const clean = (nombre || "").trim();
  if (!clean) throw new Error("Nombre vacío");
  const payload = {
    nombre: clean,
    nombreLC: clean.toLowerCase(),
    activo: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COLL), payload);
  return ref;
}

export async function ensureProveedorPorNombre({ proveedorId = "", proveedorNombre = "" } = {}) {
  const id = String(proveedorId || "").trim();
  const nombre = String(proveedorNombre || "").trim();

  if (id) return { id, nombre };
  if (!nombre) throw new Error("Captura el proveedor");

  const existentes = await findProveedoresByNombre(nombre, 10);
  const exacto = existentes.find(
    (p) => String(p?.nombre || "").trim().toLowerCase() === nombre.toLowerCase()
  );

  if (exacto) return { id: exacto.id, nombre: exacto.nombre || nombre };

  const ref = await createProveedorRapido(nombre);
  return { id: ref.id, nombre };
}

// === Read / Update / Toggle ===
export async function getProveedor(id) {
  const snap = await getDoc(doc(db, COLL, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function updateProveedor(id, data) {
  const patch = { ...data, updatedAt: serverTimestamp() };
  if (typeof data?.nombre === "string") {
    patch.nombreLC = data.nombre.trim().toLowerCase();
  }
  await updateDoc(doc(db, COLL, id), patch);
}

export async function toggleProveedorActivo(id, activo) {
  await updateProveedor(id, { activo });
}

// === Search by name (con fallback si falta el índice) ===
export async function findProveedoresByNombre(term, max = 8) {
  const t = (term || "").trim().toLowerCase();
  if (!t) return [];

  try {
    // Consulta óptima (requiere índice compuesto activo+nombreLC)
    const qy = query(
      collection(db, COLL),
      where("activo", "==", true),
      orderBy("nombreLC"),
      startAt(t),
      endAt(t + "\uf8ff"),
      qlimit(max)
    );
    const snap = await getDocs(qy);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    // Fallback: sin filtro por 'activo' en la query, filtramos en cliente.
    // Útil si aún no creaste el índice compuesto.
    const qy2 = query(
      collection(db, COLL),
      orderBy("nombreLC"),
      startAt(t),
      endAt(t + "\uf8ff"),
      qlimit(max * 2)
    );
    const snap = await getDocs(qy2);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(row => row.activo !== false)   // conserva activos (o sin campo)
      .slice(0, max);
  }
}
